import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import { PerformanceStatusBanner } from '../channels/performance-status-banner';
import { Chart } from './chart';
import { NoHiringGoal } from './no-hiring-goal';
import { NoMapping } from './no-mapping';
import { Stages } from './stages';

function FunnelContent() {
  const openingSlug = useOpeningSlug();
  const { currentHiringGoalId, showAggregateHires, showAggregateSpend } =
    useOpeningContext();

  const { data, refetch } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.funnel.details,
      openingSlug,
      currentHiringGoalId,
      showAggregateHires,
      showAggregateSpend,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingStageStats(
        openingSlug,
        currentHiringGoalId,
        showAggregateHires,
        showAggregateSpend,
      ),
  });

  const funnelData = useMemo(() => data?.sourcing_stages ?? [], [data]);

  const refetchFunnelData = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (funnelData.length === 0)
    return (
      <>
        <PerformanceStatusBanner />
        <NoMapping
          hiringGoalConversionRate={data?.hiring_goal_conversion_rate}
          refetchFunnelData={refetchFunnelData}
        />
      </>
    );

  return (
    <>
      <PerformanceStatusBanner />
      <div className="flex h-full max-h-full flex-1 gap-8 overflow-hidden pb-8">
        <Stages
          funnelData={funnelData}
          hiringGoalConversionRate={data?.hiring_goal_conversion_rate}
          automatedAction={data?.automated_action}
          refetchFunnelData={refetchFunnelData}
        />
        <Chart funnelData={funnelData} />
      </div>
    </>
  );
}

export function Funnel() {
  return (
    <ErrorBoundary FallbackComponent={NoHiringGoal}>
      <Suspense fallback={<Funnel.Skeleton />}>
        <FunnelContent />
      </Suspense>
    </ErrorBoundary>
  );
}

Funnel.Skeleton = function FunnelSkeleton() {
  return (
    <div className="flex h-full max-h-full flex-1 gap-8 overflow-hidden pb-8">
      <Stages.Skeleton />
      <Chart.Skeleton />
    </div>
  );
};
